import React, { useEffect, useMemo, useState, startTransition } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/AuthService';
import { fleetOpsService } from '../../services/FleetOpsService';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Truck, ShieldCheck, Bell, BarChart3, Wrench, Users, CheckCircle, Loader2, ArrowLeft, KeyRound } from 'lucide-react';
import '../../styles/auth.css';

interface FormData {
  email: string;
  password: string;
}

type AuthView = 'login' | 'request-reset' | 'confirm-reset';

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite')?.trim().toUpperCase() || '';
  const inviteToken = searchParams.get('token')?.trim() || '';
  const tokenInvite = useMemo(() => fleetOpsService.readTeamInviteToken(inviteToken), [inviteToken]);
  const invitedEmail = searchParams.get('email')?.trim().toLowerCase() || tokenInvite?.email || '';
  const resetActionCode = searchParams.get('oobCode')?.trim() || '';
  const resetMode = searchParams.get('mode') === 'resetPassword' || Boolean(resetActionCode);
  const [formData, setFormData] = useState<FormData>({
    email: invitedEmail,
    password: ''
  });
  const [authView, setAuthView] = useState<AuthView>(resetMode ? 'confirm-reset' : 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [resetEmail, setResetEmail] = useState(invitedEmail);
  const [verifiedResetEmail, setVerifiedResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [passwordResetForm, setPasswordResetForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const navigate = useNavigate();
  const invite = useMemo(
    () => inviteCode && formData.email ? fleetOpsService.validateTeamInvite(inviteCode, formData.email, inviteToken) : null,
    [formData.email, inviteCode, inviteToken]
  );

  useEffect(() => {
    if (invitedEmail) {
      setFormData(prev => ({ ...prev, email: invitedEmail }));
      setResetEmail(invitedEmail);
    }
  }, [invitedEmail]);

  useEffect(() => {
    if (!resetMode) {
      return;
    }

    setAuthView('confirm-reset');
    setError('');
    setResetMessage(null);

    if (!resetActionCode) {
      setResetMessage({
        type: 'error',
        text: 'This reset link is missing its security code. Request a new password reset email from the login page.'
      });
      return;
    }

    let isMounted = true;
    setResetLoading(true);

    authService.verifyPasswordResetCode(resetActionCode).then(result => {
      if (!isMounted) return;

      if ('error' in result) {
        setResetMessage({ type: 'error', text: result.error.message });
        setVerifiedResetEmail('');
      } else {
        setVerifiedResetEmail(result.email);
        setResetEmail(result.email);
        setResetMessage({ type: 'info', text: 'Reset link verified. Create a new password for your FleetFix account.' });
      }
    }).catch(() => {
      if (!isMounted) return;
      setResetMessage({ type: 'error', text: 'Unable to verify this password reset link. Request a new link and try again.' });
      setVerifiedResetEmail('');
    }).finally(() => {
      if (isMounted) {
        setResetLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [resetActionCode, resetMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleResetEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResetEmail(e.target.value);
    setResetMessage(null);
    setError('');
  };

  const handleResetPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordResetForm(prev => ({ ...prev, [name]: value }));
    setResetMessage(prev => prev?.type === 'error' ? null : prev);
  };

  const showResetRequest = () => {
    setAuthView('request-reset');
    setResetEmail(formData.email.trim().toLowerCase());
    setError('');
    setResetMessage(null);
  };

  const returnToLogin = () => {
    setAuthView('login');
    setError('');
    setResetMessage(null);
    setPasswordResetForm({ newPassword: '', confirmPassword: '' });
    if (resetMode) {
      navigate('/login', { replace: true });
    }
  };

  const handlePasswordResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = resetEmail.trim().toLowerCase();

    if (!email) {
      setResetMessage({ type: 'error', text: 'Enter the email address connected to your FleetFix account.' });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setResetMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    setResetLoading(true);
    setResetMessage(null);

    try {
      const result = await authService.resetPassword(email);

      if ('error' in result) {
        setResetMessage({ type: 'error', text: result.error.message });
        return;
      }

      setResetMessage({
        type: 'success',
        text: result.usedFallbackResetPage
          ? `Password reset email sent to ${email}. Open the secure Firebase link in that email to create a new password.`
          : `Password reset email sent to ${email}. Open the link in that email to create a new password.`
      });
      setFormData(prev => ({ ...prev, email }));
    } catch {
      setResetMessage({ type: 'error', text: 'Unable to send the reset email right now. Please try again.' });
    } finally {
      setResetLoading(false);
    }
  };

  const handlePasswordResetConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resetActionCode) {
      setResetMessage({ type: 'error', text: 'This reset link is missing its security code. Request a new link from the login page.' });
      return;
    }

    if (!verifiedResetEmail) {
      setResetMessage({ type: 'error', text: 'This reset link could not be verified. Request a new link from the login page.' });
      return;
    }

    if (passwordResetForm.newPassword.length < 6) {
      setResetMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }

    if (passwordResetForm.newPassword !== passwordResetForm.confirmPassword) {
      setResetMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }

    setResetLoading(true);
    setResetMessage(null);

    try {
      const result = await authService.confirmPasswordReset(resetActionCode, passwordResetForm.newPassword);

      if ('error' in result) {
        setResetMessage({ type: 'error', text: result.error.message });
        return;
      }

      setResetMessage({
        type: 'success',
        text: 'Your password has been changed. You can now sign in with the new password.'
      });
      setFormData({ email: verifiedResetEmail, password: '' });
      setPasswordResetForm({ newPassword: '', confirmPassword: '' });
    } catch {
      setResetMessage({ type: 'error', text: 'Unable to update your password right now. Please try again.' });
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.password) {
      setError('Please fill in all fields.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      console.log('Attempting to login...', { email: formData.email });
      const validInvite = inviteCode ? fleetOpsService.validateTeamInvite(inviteCode, formData.email.trim(), inviteToken) : null;
      if (inviteCode && !validInvite) {
        setError('This team invite is invalid, expired, revoked, or does not match this email address.');
        setIsLoading(false);
        return;
      }
      if (validInvite) {
        fleetOpsService.savePendingTeamClaim(validInvite);
      }
      
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Login timeout - taking too long')), 20000);
      });
      
      const loginPromise = authService.login({ 
        email: formData.email.trim(), 
        password: formData.password 
      });
      
      const result = await Promise.race([loginPromise, timeoutPromise]);
      
      console.log('Login result:', result);
      
      if (result && typeof result === 'object' && 'error' in result && result.error) {
        console.error('Login error:', result.error);
        const errorObj = result.error as any;
        setError(`Login failed: ${errorObj.message || 'Unknown error'}`);
        
        
        console.log('Login attempt details:', {
          email: formData.email,
          passwordLength: formData.password.length,
          errorCode: errorObj.code || 'unknown',
          errorMessage: errorObj.message || 'Unknown error'
        });
      } else {
        console.log('Login successful!', result);
        if (validInvite) {
          await fleetOpsService.acceptRemoteTeamInvite(validInvite, formData.email.trim());
        }
        
        
        setFormData({ email: '', password: '' });
        setError('');
        
        
        startTransition(() => {
          navigate('/dashboard');
        });
      }
    } catch (err: any) {
      console.error('Login error or timeout:', err);
      
      if (err.message?.includes('timeout')) {
        setError('Login is taking longer than expected. Please try again or check your credentials.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-shell">
        <section className="auth-showcase" aria-label="FleetFix overview">
          <div className="auth-brand-lockup">
            <div className="auth-brand-mark">
              <Truck size={30} />
            </div>
            <div>
              <span className="auth-brand-name">FleetFix</span>
              <span className="auth-brand-subtitle">Fleet operations platform</span>
            </div>
          </div>

          <div className="auth-hero-copy">
            <p className="auth-kicker">Maintenance, inventory, schedules, and reporting</p>
            <h1>Run the shop from one clean control center.</h1>
            <p>
              Track vehicles, maintenance work, parts, costs, and alerts without bouncing between spreadsheets.
            </p>
          </div>

          <div className="auth-metrics-grid">
            <div>
              <strong>24/7</strong>
              <span>Fleet visibility</span>
            </div>
            <div>
              <strong>PM</strong>
              <span>Compliance tracking</span>
            </div>
            <div>
              <strong>ROI</strong>
              <span>Cost reporting</span>
            </div>
          </div>

          <div className="auth-workflow-card">
            <div className="workflow-row active">
              <Wrench size={18} />
              <div>
                <strong>Brake inspection due</strong>
                <span>Unit FF-204 assigned for tomorrow</span>
              </div>
            </div>
            <div className="workflow-row">
              <Bell size={18} />
              <div>
                <strong>Parts alert</strong>
                <span>Oil filters below minimum stock</span>
              </div>
            </div>
            <div className="workflow-row">
              <BarChart3 size={18} />
              <div>
                <strong>Cost trend</strong>
                <span>Downtime down 12% this month</span>
              </div>
            </div>
          </div>
        </section>

        <div className={`auth-card ${authView !== 'login' ? 'password-reset-card' : ''}`}>
          <div className="auth-header">
            <div className="auth-logo">
              <div className="logo-icon">
                {authView === 'login' ? <ShieldCheck size={30} /> : <KeyRound size={30} />}
              </div>
              <h1>FleetFix</h1>
            </div>
            <h2>
              {authView === 'login' && 'Welcome back'}
              {authView === 'request-reset' && 'Reset your password'}
              {authView === 'confirm-reset' && 'Create a new password'}
            </h2>
            <p>
              {authView === 'login' && 'Sign in to manage your fleet operations'}
              {authView === 'request-reset' && 'Enter your account email and FleetFix will send a secure reset link.'}
              {authView === 'confirm-reset' && 'Finish the password reset from your FleetFix email link.'}
            </p>
          </div>

          {inviteCode && authView === 'login' && (
            <div className={`invite-banner ${invite ? 'valid' : 'invalid'}`}>
              <Users size={18} />
              <div>
                <strong>{invite ? 'Team invite detected' : 'Invite needs attention'}</strong>
                <span>
                  {invite
                    ? `Sign in to join this shared FleetFix workspace as ${invite.role}.`
                    : 'Use the invited email address to accept this workspace invite.'}
                </span>
              </div>
            </div>
          )}

          {authView === 'login' && (
            <>
              <form onSubmit={handleSubmit} className="auth-form">
                {error && (
                  <div className="error-alert">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-with-icon has-icon">
                    <Mail size={18} className="input-icon" />
                    <input
                      type="text"
                      inputMode="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-with-icon has-icon has-toggle">
                    <Lock size={18} className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="forgot-password-btn"
                    onClick={showResetRequest}
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={isLoading || !formData.email || !formData.password}
                >
                  {isLoading ? (
                    <>
                      <div className="loading-spinner" />
                      Signing In...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              <div className="auth-footer">
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="auth-link"
                    onClick={() => startTransition(() => navigate('/signup'))}
                    disabled={isLoading}
                  >
                    Create Account
                  </button>
                </p>
              </div>
            </>
          )}

          {authView === 'request-reset' && (
            <>
              <form onSubmit={handlePasswordResetRequest} className="auth-form">
                {resetMessage && (
                  <div className={`auth-message ${resetMessage.type}`}>
                    {resetMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    <span>{resetMessage.text}</span>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="resetEmail">Email Address</label>
                  <div className="input-with-icon has-icon">
                    <Mail size={18} className="input-icon" />
                    <input
                      type="text"
                      inputMode="email"
                      id="resetEmail"
                      name="resetEmail"
                      value={resetEmail}
                      onChange={handleResetEmailChange}
                      placeholder="Enter your account email"
                      required
                      disabled={resetLoading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={resetLoading || !resetEmail}
                >
                  {resetLoading ? (
                    <>
                      <Loader2 size={18} className="loading-spinner" />
                      Sending Link...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>

              <div className="auth-footer">
                <button type="button" className="auth-link auth-back-link" onClick={returnToLogin} disabled={resetLoading}>
                  <ArrowLeft size={15} />
                  Back to sign in
                </button>
              </div>
            </>
          )}

          {authView === 'confirm-reset' && (
            <>
              <form onSubmit={handlePasswordResetConfirm} className="auth-form">
                {resetMessage && (
                  <div className={`auth-message ${resetMessage.type}`}>
                    {resetMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    <span>{resetMessage.text}</span>
                  </div>
                )}

                {verifiedResetEmail && (
                  <div className="reset-account-line">
                    <Mail size={16} />
                    <span>{verifiedResetEmail}</span>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="input-with-icon has-icon has-toggle">
                    <Lock size={18} className="input-icon" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="newPassword"
                      name="newPassword"
                      value={passwordResetForm.newPassword}
                      onChange={handleResetPasswordChange}
                      placeholder="Enter a new password"
                      required
                      disabled={resetLoading || !verifiedResetEmail}
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      disabled={resetLoading || !verifiedResetEmail}
                      aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmNewPassword">Confirm Password</label>
                  <div className="input-with-icon has-icon has-toggle">
                    <Lock size={18} className="input-icon" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmNewPassword"
                      name="confirmPassword"
                      value={passwordResetForm.confirmPassword}
                      onChange={handleResetPasswordChange}
                      placeholder="Confirm the new password"
                      required
                      disabled={resetLoading || !verifiedResetEmail}
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={resetLoading || !verifiedResetEmail}
                      aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={resetLoading || !verifiedResetEmail || !passwordResetForm.newPassword || !passwordResetForm.confirmPassword}
                >
                  {resetLoading ? (
                    <>
                      <Loader2 size={18} className="loading-spinner" />
                      Updating Password...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>
              </form>

              <div className="auth-footer">
                <button type="button" className="auth-link auth-back-link" onClick={returnToLogin} disabled={resetLoading}>
                  <ArrowLeft size={15} />
                  Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
